import React, { useState, useEffect, useRef } from 'react';
import { X, Link as LinkIcon, Loader2, CheckCircle, Linkedin, Copy } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { useClients } from '../../hooks/useClients';
import { supabase } from '../../lib/supabaseClient';
import { mapExternalDataToRFQ } from '../../config/rfqMapping';
import { generateIndividualPost } from '../../utils/linkedinPostGenerator';

const DEFAULT_INDIVIDUAL_TEMPLATE = `🎯 New Opportunity Available

{{job_title}} | Ref: {{ref_code}}

{{description}}

📍 Location: {{locations}}
🔒 Clearance: {{clearance}}
💼 Work Arrangement: {{work_arrangement}}
📅 Start Date: {{start_date}}
⏳ Initial Term: {{term}}

Apply now: {application_url}

#hiring #opportunity #contract`;

const AddRFQModal = ({ open, onOpenChange, onSuccess, editMode = false, existingRFQ = null }) => {
  const [activeTab, setActiveTab] = useState('manual');
  const [loading, setLoading] = useState(false);
  const [fetchingUrl, setFetchingUrl] = useState(false);
  const { clients, refetch: refetchClients } = useClients();

  // LinkedIn preview state
  const [linkedinPreview, setLinkedinPreview] = useState(null); // { content, rfqId }
  const [copiedPreview, setCopiedPreview] = useState(false);
  const [pendingSuccess, setPendingSuccess] = useState(null); // { data, message }

  // Form state
  const [formData, setFormData] = useState({
    ref_code: '',
    title: '',
    client_id: '',
    clearance_required: '',
    work_arrangement: '',
    locations: [],
    internal_close_datetime: '',
    client_close_datetime: '',
    estimated_start_date: '',
    initial_term_months: '',
    extension_options: '',
    description: '',
    selection_criteria: '',
    status: 'not_invited',
    display_status: 'hidden',
    post_to_linkedin: false,
    assigned_recruiter_id: '',
  });

  // Only shown once there's more than one recruiter account - invisible
  // today, ready for when the agency isn't solo anymore.
  const [recruiters, setRecruiters] = useState([]);

  useEffect(() => {
    const loadRecruiters = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const response = await fetch(`${process.env.REACT_APP_API_URL}/rfqs/recruiters`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        });
        if (!response.ok) return;

        const data = await response.json();
        setRecruiters(data.recruiters || []);
      } catch (err) {
        console.error('Error loading recruiters:', err);
      }
    };

    if (open) loadRecruiters();
  }, [open]);

  // Track previous display_status to detect first-time change to 'visible'
  const prevDisplayStatusRef = useRef('hidden');

  // URL Import state
  const [importUrl, setImportUrl] = useState('');
  const [fetchedData, setFetchedData] = useState(null);
  const [mappedFields, setMappedFields] = useState([]);
  const [importSuccess, setImportSuccess] = useState(null); // Success message for URL import

  // Location input state
  const [locationInput, setLocationInput] = useState('');

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setFormData({
        ref_code: '',
        title: '',
        client_id: '',
        clearance_required: '',
        work_arrangement: '',
        locations: [],
        internal_close_datetime: '',
        client_close_datetime: '',
        estimated_start_date: '',
        initial_term_months: '',
        extension_options: '',
        description: '',
        selection_criteria: '',
        status: 'not_invited',
        display_status: 'hidden',
        post_to_linkedin: false,
        assigned_recruiter_id: '',
      });
      setImportUrl('');
      setFetchedData(null);
      setMappedFields([]);
      setLocationInput('');
      setActiveTab('manual');
      setImportSuccess(null);
      prevDisplayStatusRef.current = 'hidden';
    }
  }, [open]);

  // Prefill form data when in edit mode
  useEffect(() => {
    if (editMode && existingRFQ && open) {
      const displayStatus = existingRFQ.display_status !== undefined ? existingRFQ.display_status : 'hidden';
      setFormData({
        ref_code: existingRFQ.ref_code || '',
        title: existingRFQ.title || '',
        client_id: existingRFQ.client_id || '',
        clearance_required: existingRFQ.clearance_required || '',
        work_arrangement: existingRFQ.work_arrangement || '',
        locations: Array.isArray(existingRFQ.locations) ? existingRFQ.locations : [],
        internal_close_datetime: existingRFQ.internal_close_datetime?.split('+')[0] || '',
        client_close_datetime: existingRFQ.client_close_datetime?.split('+')[0] || '',
        estimated_start_date: existingRFQ.estimated_start_date || '',
        initial_term_months: existingRFQ.initial_term_months?.toString() || '',
        extension_options: existingRFQ.extension_options || '',
        description: existingRFQ.description || '',
        selection_criteria: existingRFQ.selection_criteria || '',
        status: existingRFQ.status || 'inclusion_requested',
        // Handle display_status for backward compatibility - default to 'hidden' if not present
        display_status: displayStatus,
        post_to_linkedin: existingRFQ.post_to_linkedin || false,
        assigned_recruiter_id: existingRFQ.assigned_recruiter_id || '',
      });
      // Set initial prevDisplayStatusRef to existing status to prevent auto-enable in edit mode
      prevDisplayStatusRef.current = displayStatus;
    }
  }, [editMode, existingRFQ, open]);

  // Auto-enable LinkedIn posting when display_status is FIRST set to 'visible'
  useEffect(() => {
    if (formData.display_status === 'visible' && prevDisplayStatusRef.current === 'hidden') {
      setFormData(prev => ({ ...prev, post_to_linkedin: true }));
    }
    prevDisplayStatusRef.current = formData.display_status;
  }, [formData.display_status]);

  // Clear import success message when tab changes
  useEffect(() => {
    setImportSuccess(null);
  }, [activeTab]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const addLocation = () => {
    if (locationInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        locations: [...prev.locations, locationInput.trim()],
      }));
      setLocationInput('');
    }
  };

  const removeLocation = (index) => {
    setFormData((prev) => ({
      ...prev,
      locations: prev.locations.filter((_, i) => i !== index),
    }));
  };

  const handleFetchFromUrl = async () => {
    if (!importUrl.trim()) {
      alert('Please enter a URL');
      return;
    }

    setFetchingUrl(true);
    try {
      // Call backend API to fetch data from URL
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';
      const response = await fetch(`${apiUrl}/rfqs/fetch-from-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url: importUrl }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || 'Failed to fetch data from URL');
      }

      const externalData = await response.json();

      // Check if response contains error
      if (externalData.error) {
        throw new Error(externalData.message || externalData.error);
      }

      setFetchedData(externalData);

      // Apply mapping to the fetched data
      const mapped = mapExternalDataToRFQ(externalData);

      // Lookup client_id from client name if needed
      if (mapped.client_id && typeof mapped.client_id === 'string') {
        const clientName = mapped.client_id;
        const matchingClient = clients.find(
          (c) => c.name.toLowerCase() === clientName.toLowerCase()
        );
        if (matchingClient) {
          mapped.client_id = matchingClient.id;
        } else {
          // Client not found - automatically create it with 'Pending' status
          console.log(`Client "${clientName}" not found, creating with Pending status...`);
          try {
            const { data: newClient, error: clientError } = await supabase
              .from('clients')
              .insert({
                name: clientName,
                status: 'Pending',
              })
              .select()
              .single();

            if (clientError) {
              console.error('Error creating client:', clientError);
              alert(`⚠️ Could not auto-create client "${clientName}". Please select manually.`);
              mapped.client_id = ''; // Clear so user can select manually
            } else {
              mapped.client_id = newClient.id;
              console.log(`✓ Client "${clientName}" created with Pending status`);
              // Refetch clients list to update the dropdown
              await refetchClients();
            }
          } catch (err) {
            console.error('Error creating client:', err);
            mapped.client_id = ''; // Clear so user can select manually
          }
        }
      }

      // Update form with mapped data
      setFormData((prev) => ({
        ...prev,
        ...mapped,
      }));

      // Track which fields were mapped for display
      const mappedFieldNames = Object.keys(mapped).filter(
        (key) => mapped[key] !== undefined && mapped[key] !== null && mapped[key] !== ''
      );
      setMappedFields(mappedFieldNames);

      setImportSuccess(`Data imported successfully! ${mappedFieldNames.length} fields mapped. Review the fields below.`);
    } catch (error) {
      console.error('Error fetching from URL:', error);

      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        alert(`❌ Cannot connect to backend server.\n\nError: ${error.message}`);
      } else {
        alert(`❌ Error importing data: ${error.message}\n\nCheck the URL and try again.`);
      }
    } finally {
      setFetchingUrl(false);
    }
  };

  // Fire-and-forget: the RFQ is already saved, and a failure here just means the
  // recruiter generates the profile manually from the matches page.
  const generateMatchProfile = async (rfqId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const API_URL = process.env.REACT_APP_API_URL || 'https://appsytec-backend.vercel.app/api';
      await fetch(`${API_URL}/matching/rfq/${rfqId}/generate-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ forceRegenerate: false }),
      });
    } catch (err) {
      console.error('Match profile generation failed (non-fatal):', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.ref_code || !formData.title || !formData.client_id) {
      alert('Please fill in all required fields: Ref Code, Title, and Client');
      return;
    }

    setLoading(true);
    try {
      // Prepare data for submission
      const dataToSubmit = {
        ...formData,
        locations: formData.locations.length > 0 ? formData.locations : null,
        initial_term_months: formData.initial_term_months
          ? parseInt(formData.initial_term_months)
          : null,
        internal_close_datetime: formData.internal_close_datetime || null,
        client_close_datetime: formData.client_close_datetime || null,
        estimated_start_date: formData.estimated_start_date || null,
        assigned_recruiter_id: formData.assigned_recruiter_id || null,
      };

      // Insert or Update in Supabase
      let data, error;

      if (editMode && existingRFQ) {
        // Update existing RFQ
        const result = await supabase
          .from('rfqs')
          .update(dataToSubmit)
          .eq('id', existingRFQ.id)
          .select()
          .single();
        data = result.data;
        error = result.error;
      } else {
        // Insert new RFQ
        const result = await supabase
          .from('rfqs')
          .insert([dataToSubmit])
          .select()
          .single();
        data = result.data;
        error = result.error;
      }

      if (error) throw error;

      const baseMessage = editMode ? 'RFQ updated successfully!' : 'RFQ created successfully!';

      // Extract the required skills for this role so the matches page is ready to
      // use straight away. Free keyword extraction, so there is no API cost in
      // doing this on every create.
      if (!editMode && data?.id) {
        generateMatchProfile(data.id);
      }

      // Handle LinkedIn posting
      if (formData.post_to_linkedin && data?.id) {
        // Fetch LinkedIn integration settings to check if live posting is enabled
        const { data: { user } } = await supabase.auth.getUser();
        const { data: liSettings } = await supabase
          .from('linkedin_integrations')
          .select('enabled, post_template')
          .eq('user_id', user.id)
          .maybeSingle();

        if (liSettings?.enabled) {
          // Live posting: call the API
          try {
            const { data: { session } } = await supabase.auth.getSession();
            const API_URL = process.env.REACT_APP_API_URL || 'https://appsytec-backend.vercel.app/api';
            const liRes = await fetch(`${API_URL}/linkedin/post-rfq`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({ rfqId: data.id }),
            });
            const liData = await liRes.json();
            const liMessage = liRes.ok && liData.success
              ? ' Posted to LinkedIn.'
              : ` LinkedIn post failed: ${liData.message || 'unknown error'}`;
            onSuccess?.(data, baseMessage + liMessage);
            onOpenChange(false);
          } catch (liErr) {
            console.error('LinkedIn post error:', liErr);
            onSuccess?.(data, baseMessage + ' LinkedIn post failed (network error).');
            onOpenChange(false);
          }
        } else {
          // Preview mode: generate post content and show modal
          const template = liSettings?.post_template || DEFAULT_INDIVIDUAL_TEMPLATE;
          const content = generateIndividualPost(template, {
            ...data,
            description: formData.description,
            locations: formData.locations,
          });
          setPendingSuccess({ data, message: baseMessage });
          setLinkedinPreview({ content, rfqId: data.id });
          onOpenChange(false);
        }
      } else {
        // No LinkedIn posting
        onSuccess?.(data, baseMessage);
        onOpenChange(false);
      }
    } catch (error) {
      console.error(`Error ${editMode ? 'updating' : 'creating'} RFQ:`, error);
      alert(`Error ${editMode ? 'updating' : 'creating'} RFQ: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewClose = () => {
    if (pendingSuccess) {
      onSuccess?.(pendingSuccess.data, pendingSuccess.message);
      setPendingSuccess(null);
    }
    setLinkedinPreview(null);
    setCopiedPreview(false);
  };

  const handleCopyPreview = () => {
    if (linkedinPreview?.content) {
      navigator.clipboard.writeText(linkedinPreview.content).then(() => {
        setCopiedPreview(true);
        setTimeout(() => setCopiedPreview(false), 2000);
      });
    }
  };

  return (
    <>
    {/* LinkedIn Post Preview Dialog */}
    <Dialog open={!!linkedinPreview} onOpenChange={handlePreviewClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Linkedin className="h-5 w-5 text-[#0077B5]" />
            LinkedIn Post Preview
          </DialogTitle>
          <DialogDescription>
            LinkedIn live posting is currently off. Copy this post to share manually.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-muted rounded-md p-4 text-sm whitespace-pre-wrap font-mono max-h-80 overflow-y-auto border">
            {linkedinPreview?.content}
          </div>
          {linkedinPreview?.rfqId && (
            <p className="text-xs text-muted-foreground">
              Public URL: {process.env.REACT_APP_SITE_URL || window.location.origin}/job/{linkedinPreview.rfqId}/apply
            </p>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handlePreviewClose}>
            Done
          </Button>
          <Button onClick={handleCopyPreview} className="gap-2">
            {copiedPreview ? (
              <>
                <CheckCircle className="h-4 w-4" />
                Copied!
              </>
            ) : (
              'Copy Post'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0">
        {/* Header - sticky at top */}
        <div className="p-6 pb-0">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Edit RFQ' : 'Add New RFQ'}</DialogTitle>
            <DialogDescription>
              {editMode ? 'Update the RFQ details' : 'Create a new RFQ manually or import from a URL'}
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Scrollable content */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-6">
            {!editMode ? (
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="manual">Manual Entry</TabsTrigger>
                  <TabsTrigger value="import">Import from URL</TabsTrigger>
                </TabsList>

                <TabsContent value="import" className="space-y-4">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        URL to Import From
                      </label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="https://example.com/api/rfq/123"
                          value={importUrl}
                          onChange={(e) => {
                            setImportUrl(e.target.value);
                            setImportSuccess(null); // Clear success message when URL changes
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleFetchFromUrl();
                            }
                          }}
                          disabled={fetchingUrl}
                        />
                        <Button
                          type="button"
                          onClick={handleFetchFromUrl}
                          disabled={fetchingUrl || !importUrl.trim()}
                        >
                          {fetchingUrl ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Fetching...
                            </>
                          ) : (
                            <>
                              <LinkIcon className="mr-2 h-4 w-4" />
                              Fetch
                            </>
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Enter a URL to fetch RFQ data. The system will automatically map
                        fields based on your configuration.
                      </p>
                    </div>

                    {importSuccess && (
                      <Alert className="bg-green-50 border-green-200">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <AlertDescription className="text-green-800">
                          {importSuccess}
                        </AlertDescription>
                      </Alert>
                    )}

                    {mappedFields.length > 0 && (
                      <Card>
                        <CardContent className="pt-6">
                          <p className="text-sm font-medium mb-2">Imported Fields:</p>
                          <div className="flex flex-wrap gap-2">
                            {mappedFields.map((field) => (
                              <Badge key={field} variant="secondary">
                                {field.replace(/_/g, ' ')}
                              </Badge>
                            ))}
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">
                            Review the imported data in the form below and make any necessary
                            adjustments.
                          </p>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="manual">
                  {/* Manual entry form will be rendered below */}
                </TabsContent>
              </Tabs>
            ) : null}

            {/* Common Form - shown for both tabs or direct in edit mode */}
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold">Basic Information</h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      Ref Code <span className="text-destructive">*</span>
                    </label>
                    <Input
                      name="ref_code"
                      value={formData.ref_code}
                      onChange={handleInputChange}
                      placeholder="RFQ-2024-001"
                      required
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      Status
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="not_invited">Not Invited</option>
                      <option value="inclusion_requested">Inclusion Requested</option>
                      <option value="request_declined">Request Declined</option>
                      <option value="invited">Invited</option>
                      <option value="in_progress">In Progress</option>
                      <option value="submitted">Submitted</option>
                      <option value="pending_outcome">Pending Outcome</option>
                      <option value="successful">Successful</option>
                      <option value="unsuccessful">Unsuccessful</option>
                      <option value="not_interested">Not Interested</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      Public Display Status
                    </label>
                    <select
                      name="display_status"
                      value={formData.display_status}
                      onChange={handleInputChange}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="hidden">Hidden</option>
                      <option value="visible">Visible</option>
                    </select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Visible RFQs can be viewed and applied to by candidates
                    </p>
                  </div>
                </div>

                {/* LinkedIn Posting Checkbox */}
                <div className="flex items-start space-x-3 p-4 border rounded-md bg-muted/30">
                  <input
                    type="checkbox"
                    id="post_to_linkedin"
                    checked={formData.post_to_linkedin}
                    onChange={(e) => setFormData(prev => ({ ...prev, post_to_linkedin: e.target.checked }))}
                    className="mt-1 h-4 w-4 rounded border-gray-300"
                  />
                  <div className="flex-1">
                    <label htmlFor="post_to_linkedin" className="text-sm font-medium flex items-center cursor-pointer">
                      <Linkedin className="h-4 w-4 mr-2 text-[#0077B5]" />
                      Post to LinkedIn
                    </label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Generate a LinkedIn post for this RFQ on save. When live posting is enabled in Settings → Integrations, it will post automatically. Otherwise, a preview will appear for you to copy and post manually.
                    </p>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Title <span className="text-destructive">*</span>
                  </label>
                  <Input
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Senior Software Developer"
                    required
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Client <span className="text-destructive">*</span>
                  </label>
                  <select
                    name="client_id"
                    value={formData.client_id}
                    onChange={handleInputChange}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    required
                  >
                    <option value="">Select a client...</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                </div>

                {recruiters.length > 1 && (
                  <div>
                    <label className="text-sm font-medium mb-1 block">Assigned Recruiter</label>
                    <select
                      name="assigned_recruiter_id"
                      value={formData.assigned_recruiter_id}
                      onChange={handleInputChange}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="">Default (earliest recruiter account)</option>
                      {recruiters.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.email}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Work Details */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold">Work Details</h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      Security Clearance Required
                    </label>
                    <select
                      name="clearance_required"
                      value={formData.clearance_required}
                      onChange={handleInputChange}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="">None</option>
                      <option value="Baseline">Baseline</option>
                      <option value="NV1">NV1</option>
                      <option value="NV2">NV2</option>
                      <option value="TSPV">TSPV</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      Work Arrangement
                    </label>
                    <Input
                      name="work_arrangement"
                      value={formData.work_arrangement}
                      onChange={handleInputChange}
                      placeholder="Hybrid, Remote, On-site"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Locations</label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={locationInput}
                      onChange={(e) => setLocationInput(e.target.value)}
                      placeholder="Add a location (e.g., Canberra, ACT)"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addLocation();
                        }
                      }}
                    />
                    <Button type="button" onClick={addLocation} variant="outline">
                      Add
                    </Button>
                  </div>
                  {formData.locations.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.locations.map((location, index) => (
                        <Badge key={index} variant="secondary" className="gap-1">
                          {location}
                          <X
                            className="h-3 w-3 cursor-pointer"
                            onClick={() => removeLocation(index)}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Dates & Terms */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold">Dates & Terms</h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      Internal Close Date & Time
                    </label>
                    <Input
                      type="datetime-local"
                      name="internal_close_datetime"
                      value={formData.internal_close_datetime}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      Client Close Date & Time
                    </label>
                    <Input
                      type="datetime-local"
                      name="client_close_datetime"
                      value={formData.client_close_datetime}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      Estimated Start Date
                    </label>
                    <Input
                      type="date"
                      name="estimated_start_date"
                      value={formData.estimated_start_date}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      Initial Term (months)
                    </label>
                    <Input
                      type="number"
                      name="initial_term_months"
                      value={formData.initial_term_months}
                      onChange={handleInputChange}
                      placeholder="12"
                      min="1"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Extension Options
                  </label>
                  <Textarea
                    name="extension_options"
                    value={formData.extension_options}
                    onChange={handleInputChange}
                    placeholder="E.g., 2 x 12 month extensions"
                    rows={2}
                  />
                </div>
              </div>

              {/* Description & Criteria */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold">Description & Criteria</h3>

                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Description
                  </label>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Position description, key duties, and responsibilities..."
                    rows={6}
                    className="font-mono text-xs"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Imported content may include HTML/Markdown formatting
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Selection Criteria
                  </label>
                  <Textarea
                    name="selection_criteria"
                    value={formData.selection_criteria}
                    onChange={handleInputChange}
                    placeholder="Essential and desirable criteria..."
                    rows={6}
                    className="font-mono text-xs"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Imported content may include HTML/Markdown formatting
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer - sticky at bottom */}
          <div className="border-t p-6 bg-background">
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {editMode ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>{editMode ? 'Update RFQ' : 'Create RFQ'}</>
                )}
              </Button>
            </DialogFooter>
          </div>
        </form>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default AddRFQModal;
